import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Cookies from 'js-cookie';
import {
  PlusIcon,
  UserIcon,
  UsersIcon,
  ShieldCheckIcon,
  BellIcon,
  TrashIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';

const TrainingGroups = ({
  groups,
  setShowCreateGroupModal,
  deleteGroup,
  setSelectedUsers,
  setBulkActionType,
  setShowBulkActions,
  onEditGroup,
  scrollToBulkActionsSection,
}) => {
  const { user } = useAuth();
  const baseUrl = window.location.origin;

  const buildSheetsUrl = (groupId) => {
    const token = Cookies.get('access_token');
    const params = new URLSearchParams({ group_id: String(groupId), auth_token: token || '' });
    return `${baseUrl}/api/training/schedule/sheets-export?${params.toString()}`;
  };
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Training Groups</h2>
          <p className="mt-1 text-sm text-gray-600">Manage training groups and their settings</p>
        </div>
        <button
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
          onClick={() => setShowCreateGroupModal(true)}
        >
          <PlusIcon className="h-4 w-4" />
          Create Group
        </button>
      </div>
      {groups.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No training groups</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new training group.
          </p>
        </div>
      ) : (
        <div className="admin-grid grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <div
              key={g.id}
              className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition-all hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="mb-2 flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">{g.name}</h3>
                  </div>
                  {g.description && (
                    <p className="mb-3 line-clamp-2 break-words text-sm text-gray-600">
                      {g.description}
                    </p>
                  )}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <UserIcon className="h-4 w-4" />
                      <span className="capitalize">{g.group_type}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <UsersIcon className="h-3 w-3" />
                        {g.user_memberships?.filter((m) => m.role_in_group === 'rower').length ||
                          0}{' '}
                        rowers
                      </span>
                      <span className="flex items-center gap-1">
                        <ShieldCheckIcon className="h-3 w-3" />
                        {g.user_memberships?.filter((m) => m.role_in_group === 'coach').length ||
                          0}{' '}
                        coaches
                      </span>
                    </div>
                    {g.availability_reminder_enabled && (
                      <div className="flex items-center gap-2 text-xs text-blue-600">
                        <BellIcon className="h-4 w-4" />
                        <span>Reminders enabled</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-col items-start justify-between gap-3 border-t border-gray-100 pt-3 sm:flex-row sm:items-center">
                <div className="text-xs text-gray-500">ID: {g.id}</div>
                <div className="mobile-button-group flex w-full justify-end gap-2 sm:w-auto">
                  <button
                    className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={() => onEditGroup(g)}
                    title="Edit training group"
                  >
                    <PencilIcon className="h-4 w-4" />
                    <span className="sm:hidden">Edit</span>
                  </button>
                  <button
                    className="flex items-center gap-2 rounded-md bg-orange-50 px-3 py-1.5 text-sm font-medium text-orange-700 transition-colors hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                    onClick={() => {
                      setSelectedUsers(new Set());
                      setBulkActionType(`remove-from-group-${g.id}`);
                      setShowBulkActions(true);
                      scrollToBulkActionsSection();
                    }}
                    title="Remove users from this group"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    <span className="sm:hidden">Remove Users</span>
                  </button>
                  <button
                    className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    onClick={() => deleteGroup(g)}
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span className="sm:hidden">Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default TrainingGroups;
